import React from 'react';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { OrderData, OrderService } from '../../../api';
import { ApiError } from '../../../api/core/ApiError';
import { useSafeExecute } from '../../../common/apiNotifications/safeExecute';
import { BODY_WEIGHT, FONT_SIZE, MUTED_TEXT } from '../agendaStyles';
import { formatSeconds } from '../utils/areasFormat';

type CloseBillWithNoteDialogProps = {
  order: OrderData;
  timeFormat: string;
  formatAmount: (amount: number) => React.ReactNode;
  onClose: () => void;
  onClosed: (updatedOrder: OrderData) => void;
  onStale: () => void;
};

type CloseBillFormValues = { note: string };

enum CloseBillErrorDetail {
  ALREADY_CLOSED = 'Balance already closed',
  NO_OPEN_BALANCE = 'Order has no open balance',
  NOT_ACTIVE = 'Order is not active',
  BALANCE_CHANGED = 'Balance changed, try again',
}

const STALE_ERROR_KEYS: Record<CloseBillErrorDetail, string> = {
  [CloseBillErrorDetail.ALREADY_CLOSED]: 'calendar.areas_view.close_bill_error_already_closed',
  [CloseBillErrorDetail.NO_OPEN_BALANCE]: 'calendar.areas_view.close_bill_error_no_balance',
  [CloseBillErrorDetail.NOT_ACTIVE]: 'calendar.areas_view.close_bill_error_not_active',
  [CloseBillErrorDetail.BALANCE_CHANGED]: 'calendar.areas_view.close_bill_error_balance_changed',
};

const isStaleErrorDetail = (detail: unknown): detail is CloseBillErrorDetail =>
  typeof detail === 'string' && detail in STALE_ERROR_KEYS;

export const CloseBillWithNoteDialog = ({
  order,
  timeFormat,
  formatAmount,
  onClose,
  onClosed,
  onStale,
}: CloseBillWithNoteDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const safeExecute = useSafeExecute();

  const validationSchema = yup.object().shape({
    note: yup.string().trim().required(t('calendar.areas_view.close_bill_note_required')),
  });

  const mapCloseError = (error: ApiError): string => {
    const detail = error.body?.detail;
    if (isStaleErrorDetail(detail)) {
      return t(STALE_ERROR_KEYS[detail]);
    }
    return typeof detail === 'string' ? detail : t('api.error.default');
  };

  const onSubmit = async (values: CloseBillFormValues) => {
    try {
      const updatedOrder = await safeExecute({
        apiCall: () => OrderService.closeOrderBalance({ order_id: order.id, note: values.note.trim() }),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: mapCloseError,
      });
      onClosed(updatedOrder);
    } catch (error) {
      if (error instanceof ApiError && isStaleErrorDetail(error.body?.detail)) {
        onStale();
      }
    }
  };

  const name = [order.firstname, order.lastname].filter(Boolean).join(' ');

  return (
    <Formik initialValues={{ note: '' }} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, errors, touched, handleChange, handleBlur, submitForm, isSubmitting }) => (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs" fullScreen={isSmallScreen}>
          <DialogTitle>{t('calendar.areas_view.close_bill_title')}</DialogTitle>
          <DialogContent>
            <Grid container direction="column" wrap="nowrap" gap={2}>
              <Grid
                container
                alignItems="center"
                wrap="nowrap"
                gap={1.5}
                sx={{ paddingBlock: 1.25, paddingInline: 1.75, borderRadius: '6px', backgroundColor: 'mb.ground' }}>
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.body }}>
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: 'inherit' }}>
                    {name || t('calendar.areas_view.unnamed_order')}
                  </Typography>
                  {order.order_number !== undefined && (
                    <Typography
                      component="span"
                      dir="ltr"
                      sx={{ fontSize: FONT_SIZE.caption, color: MUTED_TEXT, marginInlineStart: 0.75 }}>
                      #{order.order_number}
                    </Typography>
                  )}
                  <Typography
                    component="span"
                    dir="ltr"
                    sx={{ fontSize: FONT_SIZE.caption, color: MUTED_TEXT, marginInlineStart: 0.75 }}>
                    {formatSeconds(order.time ?? 0, timeFormat)}
                  </Typography>
                </Typography>
                <Typography dir="ltr" sx={{ fontWeight: 700, color: 'error.main', flexShrink: 0 }}>
                  {formatAmount(order.balance_due ?? 0)}
                </Typography>
              </Grid>
              <Typography sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, color: MUTED_TEXT }}>
                {t('calendar.areas_view.close_bill_explain')}
              </Typography>
              <TextField
                required
                multiline
                minRows={3}
                autoFocus
                name="note"
                label={t('calendar.areas_view.close_bill_note_label')}
                value={values.note}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.note && Boolean(errors.note)}
                helperText={touched.note && errors.note ? errors.note : t('calendar.areas_view.close_bill_note_hint')}
              />
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onClose} sx={{ flex: { xs: 1, md: 'none' } }}>
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              color="error"
              onClick={submitForm}
              sx={{ boxShadow: 'none', flex: { xs: 2, md: 'none' } }}>
              {t('calendar.areas_view.close_bill_confirm')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};
